import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Checkbox,
  Modal,
  Text,
  useTheme,
} from '@querodelivery/uni-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form } from '@radix-ui/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import type { LatLng } from 'leaflet';
import { Helmet } from 'react-helmet';
import { Breadcrumb } from '@/_components/breadcrumb';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import MapWithPin from '@/Components/map-with-pin/MapWithPin';
import { useGetPlaceById } from '@/_features/places/api/get-place-by-id';
import { usePostCloudActivation } from '@/_features/places/api/post-cloud-activation';
import { useQueroDeliveryNeighborhoods } from '@/_features/places/hooks/use-quero-delivery-neighborhoods';
import {
  ENUM_CLOUD_ACTIVATION_REQUEST_STATUS,
  GET_PLACES,
} from '@/_features/places/constants';
import { GET_PLACE_ID } from '@/_features/places/api/constants';
import {
  PRE_REGISTRATION_TABS,
  PRE_REGISTRATION_TAB_SEARCH_PARAM,
} from '@/_features/motoboys/pre-registration/constants';
import { useCities } from '@/_hooks/use-cities';
import { GOOGLE_MAPS_EMBED_API_KEY } from '@/_config/env';
import type { ILocation } from '@/Interfaces/Data';
import { celularLabel, cepLabel, formatAddress } from '@/Utils/FnUtils';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import {
  findDropdownOptionBySuburbName,
  getSuburbFromGoogleComponents,
  GOOGLE_ADDRESS_TYPES,
  PLACES_PRE_REGISTRATION_ADDRESS_AUTOCOMPLETE_ID,
  PLACES_PRE_REGISTRATION_FORM_SCHEMA,
  type GooglePlace,
  type PlacesPreRegistrationFormValues,
} from './constants';
import styles from './places-pre-registration.module.scss';

type CheckboxValue = 'aprovado' | 'baseReserva';

const PARTNERS_LIST_PATH = `/precadastro/esteira?${PRE_REGISTRATION_TAB_SEARCH_PARAM}=${PRE_REGISTRATION_TABS.PARTNERS}`;

function getPlaceDefaultPosition(
  placeLocation?: ILocation,
  cityLocation?: ILocation,
): ILocation | undefined {
  if (placeLocation?.latitude && placeLocation?.longitude) {
    return placeLocation;
  }

  if (cityLocation?.latitude && cityLocation?.longitude) {
    return cityLocation;
  }

  return undefined;
}

export const PlacesPreRegistration = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const colors = useTheme().color;
  const { placeId } = useParams<{ placeId: string }>();
  const [selectedChecked, setSelectedChecked] =
    useState<CheckboxValue>('aprovado');
  const [
    isActionConfirmationModalVisible,
    setIsActionConfirmationModalVisible,
  ] = useState(false);
  const [isMapConfirmModalVisible, setIsMapConfirmModalVisible] =
    useState(false);
  const [cityPosition, setCityPosition] = useState<ILocation>();
  const [orderPosition, setOrderPosition] = useState<ILocation>();
  const [newPosition, setNewPosition] = useState({ lat: 0, lng: 0 });
  const [loadingMap, setLoadingMap] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [isAddressConfirmationVisible, setIsAddressConfirmationVisible] =
    useState(false);
  const [pendingSuburbMatch, setPendingSuburbMatch] = useState<string | null>(
    null,
  );

  const { data: place } = useGetPlaceById(
    { placeId: placeId || '' },
    { enabled: !!placeId },
  );

  const { cities } = useCities();

  const placeCityLocation = cities?.find(
    city => city._id === place?.municipioId,
  )?.localizacao;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<PlacesPreRegistrationFormValues>({
    resolver: yupResolver(PLACES_PRE_REGISTRATION_FORM_SCHEMA),
    defaultValues: {
      endereco: '',
      logradouro: '',
      numero: '',
      bairro: '',
      cep: '',
      cityName: '',
      neighborhoodId: '',
      complemento: '',
      referencia: '',
    },
  });

  const cityName = watch('cityName', '');
  const neighborhoodIdWatched = watch('neighborhoodId');

  const { neighborhoodsOptions, isNeighborhoodsQueryFetched } =
    useQueroDeliveryNeighborhoods({
      cityName: cityName || place?.cityName || '',
    });

  const {
    mutate: postCloudActivation,
    isLoading: isLoadingPostCloudActivation,
  } = usePostCloudActivation({
    onSuccess: () => {
      setIsActionConfirmationModalVisible(false);
      queryClient.invalidateQueries({ queryKey: [GET_PLACES] });
      queryClient.invalidateQueries([GET_PLACE_ID, { placeId }]);
      navigate(PARTNERS_LIST_PATH);
    },
  });

  useEffect(() => {
    if (!place) {
      return;
    }

    const defaultPosition = getPlaceDefaultPosition(
      place.localizacao,
      placeCityLocation,
    );

    if (defaultPosition) {
      setCityPosition(defaultPosition);
      setOrderPosition(defaultPosition);
    }

    reset({
      endereco: place.endereco ? formatAddress(place.endereco) : '',
      logradouro: place.endereco?.logradouro || '',
      numero: place.endereco?.numero || '',
      bairro: place.endereco?.bairro || '',
      cep: place.endereco?.cep || '',
      cityName: place.cityName || '',
      neighborhoodId: '',
      complemento: place.endereco?.complemento || '',
      referencia: place.endereco?.referencia || '',
    });

    if (place.endereco?.bairro) {
      setPendingSuburbMatch(place.endereco.bairro);
    }

    if (place.endereco) {
      setIsAddressConfirmationVisible(true);
    }

    const cloudActivationStatus = place.cloudActivationRequest?.status;

    setSelectedChecked(
      cloudActivationStatus ===
        ENUM_CLOUD_ACTIVATION_REQUEST_STATUS.RESERVE_BASE
        ? 'baseReserva'
        : 'aprovado',
    );
  }, [place, placeCityLocation]);

  useEffect(() => {
    if (!neighborhoodIdWatched || !neighborhoodsOptions.length) {
      return;
    }

    const foundByValue = neighborhoodsOptions.find(
      option => option.value === neighborhoodIdWatched,
    );
    if (foundByValue) {
      return;
    }

    const foundByText = neighborhoodsOptions.find(
      option =>
        option.text.toLowerCase() === neighborhoodIdWatched.toLowerCase(),
    );

    if (foundByText) {
      setValue('neighborhoodId', foundByText.value);
    }
  }, [neighborhoodIdWatched, neighborhoodsOptions]);

  useEffect(() => {
    if (!pendingSuburbMatch) {
      return;
    }

    if (!cityName && !place?.cityName) {
      setPendingSuburbMatch(null);
      return;
    }

    if (!isNeighborhoodsQueryFetched) {
      return;
    }

    const match = findDropdownOptionBySuburbName(
      pendingSuburbMatch,
      neighborhoodsOptions,
    );

    if (match) {
      setValue('neighborhoodId', match.value);
    }

    setPendingSuburbMatch(null);
  }, [
    cityName,
    isNeighborhoodsQueryFetched,
    neighborhoodsOptions,
    pendingSuburbMatch,
    place?.cityName,
  ]);

  useEffect(() => {
    if (
      place?.cloudActivationRequest?.status ===
      ENUM_CLOUD_ACTIVATION_REQUEST_STATUS.APPROVED
    ) {
      return;
    }

    const timer = setTimeout(() => {
      const element = document.getElementById(
        PLACES_PRE_REGISTRATION_ADDRESS_AUTOCOMPLETE_ID,
      );

      if (!element || !window.google?.maps?.places) {
        return;
      }

      const autocomplete = new window.google.maps.places.Autocomplete(
        element as HTMLInputElement,
        { fields: ['address_components', 'geometry', 'formatted_address'] },
      );

      autocomplete.addListener('place_changed', () => {
        const googlePlace: GooglePlace = autocomplete.getPlace();

        if (googlePlace.address_components) {
          const { address_components, formatted_address } = googlePlace;

          setValue('endereco', formatted_address || '');

          const parsedCityName =
            address_components.find(component =>
              component.types.includes(GOOGLE_ADDRESS_TYPES.cidade),
            )?.long_name || '';
          const streetNumber =
            address_components.find(component =>
              component.types.includes(GOOGLE_ADDRESS_TYPES.numero),
            )?.long_name || '';
          const streetName =
            address_components.find(component =>
              component.types.includes(GOOGLE_ADDRESS_TYPES.endereco),
            )?.long_name || '';
          const neighborhood =
            address_components.find(component =>
              component.types.includes(GOOGLE_ADDRESS_TYPES.bairro),
            )?.long_name || '';
          const zipCode =
            address_components.find(component =>
              component.types.includes(GOOGLE_ADDRESS_TYPES.cep),
            )?.long_name || '';

          const suburbFromComponents =
            getSuburbFromGoogleComponents(address_components);

          const actualNeighborhood =
            suburbFromComponents || neighborhood || parsedCityName;
          const actualZipCode = zipCode || place?.endereco?.cep || '';
          const actualStreetName = streetName || actualNeighborhood;

          setValue('numero', streetNumber);
          setValue('logradouro', actualStreetName);
          setValue('bairro', actualNeighborhood);
          setValue('cityName', parsedCityName);
          setValue('cep', actualZipCode);

          if (suburbFromComponents || actualNeighborhood) {
            setPendingSuburbMatch(suburbFromComponents || actualNeighborhood);
          } else {
            setPendingSuburbMatch(null);
          }

          clearErrors('endereco');
          setIsAddressConfirmationVisible(true);
        }

        if (googlePlace.geometry?.location) {
          setLoadingMap(true);

          const latitude = googlePlace.geometry.location.lat();
          const longitude = googlePlace.geometry.location.lng();
          const nextPosition = {
            latitude: Number(latitude.toPrecision(9)),
            longitude: Number(longitude.toPrecision(9)),
          };

          setCityPosition(nextPosition);
          setOrderPosition(nextPosition);

          setTimeout(() => setLoadingMap(false), 1000);
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [place?.cloudActivationRequest?.status, place?.endereco?.cep]);

  async function reverseLatLng(lat: number, lng: number) {
    setLoadingAddress(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      );
      const location = response ? await response.json() : null;

      if (!location) {
        return;
      }

      const suburbName =
        location.address?.suburb || location.address?.city_district || '';

      setValue('logradouro', location.address?.road || '');
      setValue('bairro', suburbName);
      setValue('cep', location.address?.postcode || '');
      setValue('numero', location.address?.house_number || '');
      setValue(
        'cityName',
        location.address?.town || location.address?.city_district || '',
      );
      setValue(
        'endereco',
        `${location.address?.road || ''}, ${
          location.address?.house_number || ''
        } - ${
          location.address?.suburb || location.address?.city_district || ''
        }, ${
          location.address?.town || location.address?.city_district || ''
        } - ${location.address?.state || ''}, ${
          location.address?.country || ''
        }`.replace(/undefined/g, ''),
      );

      if (suburbName) {
        setPendingSuburbMatch(suburbName);
      } else {
        setPendingSuburbMatch(null);
      }

      clearErrors('endereco');
      setIsAddressConfirmationVisible(true);
    } finally {
      setLoadingAddress(false);
    }
  }

  function handleChangePin(position: LatLng) {
    if (isPlaceApproved) {
      return;
    }

    setNewPosition({ lat: position.lat, lng: position.lng });
    setIsMapConfirmModalVisible(true);
  }

  function confirmPositionModal() {
    const nextPosition = {
      latitude: Number(newPosition.lat.toPrecision(9)),
      longitude: Number(newPosition.lng.toPrecision(9)),
    };

    setOrderPosition(nextPosition);
    reverseLatLng(newPosition.lat, newPosition.lng);
    setIsAddressConfirmationVisible(true);
    setIsMapConfirmModalVisible(false);
  }

  const handleOpenConfirmationModal = handleSubmit(() => {
    setIsActionConfirmationModalVisible(true);
  });

  const handleConfirmSubmit: SubmitHandler<
    PlacesPreRegistrationFormValues
  > = data => {
    if (!placeId || !orderPosition) {
      return;
    }

    const neighborhoodName =
      neighborhoodsOptions.find(option => option.value === data.neighborhoodId)
        ?.text ??
      data.bairro?.trim() ??
      '';

    if (!neighborhoodName) {
      return;
    }

    postCloudActivation({
      establishmentId: placeId,
      isActivateCloud: selectedChecked === 'aprovado',
      isReserveBase: selectedChecked === 'baseReserva',
      address: {
        localizacao: {
          latitude: parseFloat(orderPosition.latitude.toPrecision(9)),
          longitude: parseFloat(orderPosition.longitude.toPrecision(9)),
        },
        street: data.logradouro,
        number: String(data.numero ?? ''),
        neighborhood: neighborhoodName,
        zipCode: data.cep,
        cityName: data.cityName,
        complement: data.complemento || undefined,
        reference: data.referencia || undefined,
      },
    });
  };

  const isPlaceApproved =
    place?.cloudActivationRequest?.status ===
    ENUM_CLOUD_ACTIVATION_REQUEST_STATUS.APPROVED;

  const isFieldDisabled = loadingAddress || isPlaceApproved;

  const disabledFieldClassName = isFieldDisabled
    ? styles.disabledCursor
    : undefined;

  const isSubmitDisabled =
    isSubmitting ||
    isLoadingPostCloudActivation ||
    loadingAddress ||
    !orderPosition ||
    !placeId ||
    isPlaceApproved;

  const hasPlaceLocation = Boolean(cityPosition && orderPosition);

  return (
    <>
      <Helmet>
        {!window.google?.maps ? (
          <script
            src={`https://maps.googleapis.com/maps/api/js?libraries=places&key=${GOOGLE_MAPS_EMBED_API_KEY}&language=pt-BR`}
          />
        ) : null}
      </Helmet>

      <Form
        className={`${styles.mainContainer} container`}
        onSubmit={handleSubmit(handleConfirmSubmit)}
      >
        <div className={styles.breadcrumbWrapper}>
          <button
            type="button"
            className={styles.breadcrumbBack}
            onClick={() => navigate(PARTNERS_LIST_PATH)}
            aria-label="Voltar"
          >
            <FontAwesomeIcon icon={faChevronLeft} size="sm" />
          </button>
          <Breadcrumb showBackButtonIcon={false}>
            <Link to={PARTNERS_LIST_PATH}>
              <Text variant="bodyMd" fontWeight="medium">
                Esteira de aprovação
              </Text>
            </Link>
            <Text variant="bodyMd" fontWeight="medium">
              Parceiro
            </Text>
            <Link data-actived className="text-gray" relative="path" to="">
              {place?.nomeExibicao}
            </Link>
          </Breadcrumb>
        </div>

        <div className={styles.container}>
          <div className={styles.addressInfoContainer}>
            <Text variant="bodySm" tone="highlight" fontWeight="bold">
              Localização
            </Text>
            <div className={styles.addressContainer}>
              <Controller
                name="endereco"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <TextInput
                    id={PLACES_PRE_REGISTRATION_ADDRESS_AUTOCOMPLETE_ID}
                    name={name}
                    label="Endereço de entrega"
                    labelClassName={styles.textInputLabel}
                    containerClassName={disabledFieldClassName}
                    placeholder="Aracaju, Jardins, rua Santos Dumont, nº 35"
                    value={value ?? ''}
                    disabled={isFieldDisabled}
                    errorMessage={errors.endereco?.message}
                    error={!!errors.endereco?.message}
                    onChange={event => {
                      setPendingSuburbMatch(null);
                      setIsAddressConfirmationVisible(false);
                      setValue('logradouro', '');
                      setValue('bairro', '');
                      setValue('cep', '');
                      setValue('numero', '');
                      setValue('cityName', '');
                      setValue('neighborhoodId', '');
                      onChange(event.target.value);
                    }}
                  />
                )}
              />

              {isAddressConfirmationVisible ? (
                <div className={styles.addressConfirmationContainer}>
                  <Text variant="bodySm" tone="highlight" fontWeight="bold">
                    Confirme ou complete os dados do endereço.
                  </Text>
                  <div className={styles.addressConfirmationFields}>
                    <div className={styles.addressConfirmationRow}>
                      <Controller
                        name="logradouro"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <TextInput
                            name={name}
                            label="Logradouro"
                            labelClassName={styles.textInputLabel}
                            containerClassName={disabledFieldClassName}
                            placeholder="Rua, avenida..."
                            value={value ?? ''}
                            disabled={isFieldDisabled}
                            errorMessage={errors.logradouro?.message}
                            error={!!errors.logradouro?.message}
                            onChange={event => onChange(event.target.value)}
                          />
                        )}
                      />
                      <Controller
                        name="numero"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <TextInput
                            name={name}
                            label="Número"
                            labelClassName={styles.textInputLabel}
                            containerClassName={disabledFieldClassName}
                            placeholder="Nº"
                            value={value ?? ''}
                            disabled={isFieldDisabled}
                            errorMessage={errors.numero?.message}
                            error={!!errors.numero?.message}
                            onChange={event => onChange(event.target.value)}
                          />
                        )}
                      />
                    </div>
                    <div className={styles.addressConfirmationRowThree}>
                      <Controller
                        name="cep"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <TextInput
                            name={name}
                            label="CEP"
                            labelClassName={styles.textInputLabel}
                            containerClassName={disabledFieldClassName}
                            placeholder="00000-000"
                            value={value ?? ''}
                            disabled={isFieldDisabled}
                            errorMessage={errors.cep?.message}
                            error={!!errors.cep?.message}
                            onChange={event =>
                              onChange(cepLabel(event.target.value))
                            }
                          />
                        )}
                      />
                      <Controller
                        name="neighborhoodId"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DropdownInput
                            label="Bairro"
                            labelClassName={styles.textInputLabel}
                            containerClassName={
                              isFieldDisabled || (!cityName && !place?.cityName)
                                ? styles.disabledCursor
                                : undefined
                            }
                            placeholder={
                              cityName || place?.cityName
                                ? 'Bairro'
                                : 'Informe a cidade primeiro'
                            }
                            disabled={
                              isFieldDisabled || (!cityName && !place?.cityName)
                            }
                            options={neighborhoodsOptions}
                            value={value}
                            errorMessage={errors.neighborhoodId?.message}
                            error={!!errors.neighborhoodId?.message}
                            onChange={selectedValue =>
                              onChange(selectedValue as string)
                            }
                          />
                        )}
                      />
                      <Controller
                        name="cityName"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <TextInput
                            name={name}
                            label="Cidade"
                            labelClassName={styles.textInputLabel}
                            containerClassName={disabledFieldClassName}
                            placeholder="Cidade"
                            value={value ?? ''}
                            disabled={isFieldDisabled}
                            errorMessage={errors.cityName?.message}
                            error={!!errors.cityName?.message}
                            onChange={event => onChange(event.target.value)}
                          />
                        )}
                      />
                    </div>
                    <div className={styles.addressConfirmationRowTwo}>
                      <Controller
                        name="complemento"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <TextInput
                            name={name}
                            label="Complemento"
                            labelClassName={styles.textInputLabel}
                            containerClassName={disabledFieldClassName}
                            placeholder="Apto, bloco..."
                            value={value ?? ''}
                            disabled={isFieldDisabled}
                            onChange={event => onChange(event.target.value)}
                          />
                        )}
                      />
                      <Controller
                        name="referencia"
                        control={control}
                        render={({ field: { onChange, value, name } }) => (
                          <TextInput
                            name={name}
                            label="Referência"
                            labelClassName={styles.textInputLabel}
                            containerClassName={disabledFieldClassName}
                            placeholder="Ponto de referência"
                            value={value ?? ''}
                            disabled={isFieldDisabled}
                            onChange={event => onChange(event.target.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className={styles.mapContainer}>
            <Text variant="bodySm" tone="highlight" fontWeight="bold">
              Confirme o endereço do destino no mapa:
            </Text>
            <div
              className={`${styles.map} ${
                isPlaceApproved ? styles.mapReadOnly : ''
              }`}
            >
              {hasPlaceLocation ? (
                <MapWithPin
                  popupLabel="Local do estabelecimento"
                  onChangePin={handleChangePin}
                  mainPosition={cityPosition!}
                  objectPosition={orderPosition}
                  loadingMap={loadingMap}
                  zoom={15}
                />
              ) : (
                <Skeleton className={styles.mapSkeleton} />
              )}
            </div>
          </div>

          <div className={styles.partnerInfoContainer}>
            <Text variant="bodySm" tone="highlight" fontWeight="bold">
              Informações do parceiro
            </Text>
            <div className={styles.partnerInfo}>
              <TextInput
                name="estabelecimento"
                label="Estabelecimento"
                labelClassName={styles.textInputLabel}
                containerClassName={styles.disabledCursor}
                value={place?.nomeExibicao}
                disabled
              />
              <TextInput
                name="telefone"
                label="Telefone"
                labelClassName={styles.textInputLabel}
                containerClassName={styles.disabledCursor}
                value={place?.celular ? celularLabel(place.celular) : '- - -'}
                disabled
              />
            </div>
          </div>

          <div className={styles.bottomContainer}>
            <div
              className={`${styles.checkboxes} ${
                isPlaceApproved ? styles.checkboxesReadOnly : ''
              }`}
            >
              <Checkbox
                id="aprovado"
                name="aprovado"
                label="Aprovado"
                labelPosition="right"
                checked={selectedChecked === 'aprovado'}
                disabled={isPlaceApproved}
                onChange={() => setSelectedChecked('aprovado')}
              />
              <Checkbox
                id="baseReserva"
                name="baseReserva"
                label="Enviar para a base reserva"
                labelPosition="right"
                checked={selectedChecked === 'baseReserva'}
                disabled={isPlaceApproved}
                onChange={() => setSelectedChecked('baseReserva')}
              />
            </div>
            <Button
              variant="primary"
              className={styles.submitButton}
              size="xl"
              type="button"
              loading={isLoadingPostCloudActivation}
              disabled={isSubmitDisabled}
              onClick={handleOpenConfirmationModal}
            >
              concluir
            </Button>
          </div>
        </div>

        <Modal
          isOpen={isActionConfirmationModalVisible}
          onClose={() => setIsActionConfirmationModalVisible(false)}
          width="35%"
        >
          <div className={styles.modalContent}>
            <FontAwesomeIcon
              icon={faBell}
              color={colors['color-base-border']}
              size="3x"
            />
            <Text variant="headingMd" fontWeight="bold" tone="base">
              {selectedChecked === 'aprovado'
                ? 'Você está ativando o Log nuvem para esse parceiro.'
                : 'Você está enviando esse parceiro para a base reserva.'}
            </Text>

            <Text variant="bodySm" tone="secondary" fontWeight="medium">
              {selectedChecked === 'aprovado'
                ? 'certeza que deseja continuar? ao confirmar você deve entrar em contato com o parceiro e agendar seu treinamento.'
                : 'Certeza que deseja continuar?'}
            </Text>
            <div className={styles.modalButtonsContainer}>
              <Button
                variant="outline"
                type="button"
                size="xl"
                width="100%"
                onClick={() => setIsActionConfirmationModalVisible(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                size="xl"
                width="100%"
                loading={isLoadingPostCloudActivation}
                disabled={isSubmitDisabled}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </Modal>
      </Form>

      <Modal
        header="Tem certeza que deseja trocar o endereço?"
        isOpen={isMapConfirmModalVisible}
        onClose={() => setIsMapConfirmModalVisible(false)}
      >
        <div className={styles.modalButtonsContainer}>
          <Button
            variant="outline"
            size="xl"
            width="100%"
            onClick={() => setIsMapConfirmModalVisible(false)}
          >
            Não
          </Button>
          <Button
            variant="primary"
            size="xl"
            width="100%"
            onClick={confirmPositionModal}
          >
            Sim
          </Button>
        </div>
      </Modal>
    </>
  );
};
